import type { CarrierProviderAddOnService } from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { getAddOnDetailRows } from "@/lib/add-on-service-format";

interface AddOnDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service: CarrierProviderAddOnService;
}

/**
 * Full read-only detail for one provider add-on service. The list endpoint
 * already returns the type-specific detail block + label-carrier access, so
 * this is a pure presentation drill-in (no extra fetch).
 */
export function AddOnDetailDialog({
  open,
  onOpenChange,
  service,
}: AddOnDetailDialogProps) {
  const detailRows = getAddOnDetailRows(service);

  return (
    <DetailDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={service.name}
      description={`${service.provider.name} · ${service.code}`}
    >
      <div className="space-y-4 text-sm">
        <div className="space-y-1">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Code</span>
            <span className="font-mono text-xs">{service.code}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Provider</span>
            <span>{service.provider.name}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Status</span>
            <StatusBadge status={service.active ? "ACTIVE" : "INACTIVE"} />
          </div>
        </div>

        <div>
          <p className="text-muted-foreground mb-1">Details</p>
          {detailRows.length === 0 ? (
            <p className="text-muted-foreground">No type-specific details.</p>
          ) : (
            <dl className="rounded-md border divide-y">
              {detailRows.map((row) => (
                <div
                  key={row.key}
                  className="flex justify-between gap-4 px-3 py-2"
                >
                  <dt className="text-muted-foreground">{row.label}</dt>
                  <dd className="text-right">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <div>
          <p className="text-muted-foreground mb-1">Label-carrier access</p>
          {service.labelCarrierAccess.length === 0 ? (
            <p className="text-muted-foreground">No label carriers.</p>
          ) : (
            <ul className="rounded-md border divide-y">
              {service.labelCarrierAccess.map((access) => (
                <li
                  key={access.id}
                  className="flex justify-between gap-4 px-3 py-2"
                >
                  <span>{access.labelCarrier.name}</span>
                  <span
                    className={
                      access.enabled ? "text-primary" : "text-muted-foreground"
                    }
                  >
                    {access.enabled ? "Enabled" : "Disabled"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t pt-4">
          <Button
            variant="outline"
            size="touch"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </DetailDrawer>
  );
}
